import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCurrentUser } from '../account/useAuth';
import { AuthApiError } from '../account/authApi';
import { useClassrooms, useCreateClassroom } from './useTeacher';
import './TeacherDashboard.css';

/**
 * Teacher landing page — `/teacher`.
 *
 * Lists the teacher's classrooms with the class code prominent (it's
 * what the kid types to sign in, so it earns the visual real-estate
 * over a UUID). New-classroom creation is inline — no modal — so a
 * teacher can spin up a room in two clicks.
 *
 * Non-teachers see a kid-friendly notice; we don't want a 403 page
 * to feel like a punishment. They navigate elsewhere from here.
 */
export function TeacherDashboard(): JSX.Element {
  const { data: user, isPending: userPending } = useCurrentUser();
  const classrooms = useClassrooms();
  const createMutation = useCreateClassroom();
  const [name, setName] = useState('');

  if (userPending) {
    return <div className="bl-teacher" data-testid="teacher-loading" />;
  }

  if (!user) {
    return (
      <div className="bl-teacher">
        <h1 className="bl-teacher__title">Teacher dashboard</h1>
        <p className="bl-teacher__notice">Sign in as a teacher to see your classrooms.</p>
      </div>
    );
  }

  if (user.role !== 'teacher' && user.role !== 'admin') {
    return (
      <div className="bl-teacher">
        <h1 className="bl-teacher__title">Teacher dashboard</h1>
        <p className="bl-teacher__notice">
          This page is for teachers. <Link to="/studio">Open the studio →</Link>
        </p>
      </div>
    );
  }

  // 401 from listClassrooms means the cookie was cleared in another
  // tab. Surface kid-friendly copy + a sign-in nudge.
  if (
    classrooms.isError &&
    classrooms.error instanceof AuthApiError &&
    classrooms.error.status === 401
  ) {
    return (
      <div className="bl-teacher">
        <h1 className="bl-teacher__title">Teacher dashboard</h1>
        <p className="bl-teacher__notice">
          Your sign-in expired. Refresh the page and sign in again.
        </p>
      </div>
    );
  }

  const handleCreate = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!name.trim() || createMutation.isPending) return;
    createMutation.mutate(name.trim(), {
      onSuccess: () => setName(''),
    });
  };

  return (
    <div className="bl-teacher">
      <header className="bl-teacher__header">
        <div>
          <h1 className="bl-teacher__title">Your classrooms</h1>
          <p className="bl-teacher__subtitle">
            Each classroom has its own gallery, roster, and review queue.
          </p>
        </div>
        <Link to="/teacher/lessons" className="bl-teacher__lesson-link">
          Lesson library →
        </Link>
      </header>

      <form onSubmit={handleCreate} className="bl-teacher__create">
        <label htmlFor="teacher-new-classroom" className="bl-teacher__label">
          New classroom
        </label>
        <div className="bl-teacher__create-row">
          <input
            id="teacher-new-classroom"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Period 3 — Image classifier"
            className="bl-teacher__input"
            maxLength={120}
          />
          <button
            type="submit"
            className="bl-teacher__btn"
            disabled={!name.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
        {createMutation.error instanceof Error ? (
          <p className="bl-teacher__error">{createMutation.error.message}</p>
        ) : null}
      </form>

      {classrooms.isPending ? (
        <p className="bl-teacher__notice">Loading…</p>
      ) : classrooms.isError ? (
        <p className="bl-teacher__error">
          Couldn&apos;t load your classrooms. Try again in a moment.
        </p>
      ) : classrooms.data && classrooms.data.length > 0 ? (
        <ul className="bl-teacher__list" data-testid="classroom-list">
          {classrooms.data.map((c) => (
            <li key={c.id} className="bl-teacher__row">
              <Link to={`/teacher/classrooms/${c.id}`} className="bl-teacher__row-name">
                {c.name}
              </Link>
              <span className="bl-teacher__code" aria-label="class code">
                {c.class_code}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="bl-teacher__empty">
          <p>You haven&apos;t made a classroom yet.</p>
          <p className="bl-teacher__empty-hint">
            Make one above — kids join with the class code, no email needed.
          </p>
        </div>
      )}
    </div>
  );
}
